import { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "@/stores";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface StageUpdate {
  name_en: string | null;
}

const DesignStagesTranslationsTable = observer(() => {
  const { designStagesStore } = useStores();
  const [stageUpdates, setStageUpdates] = useState<Record<number, StageUpdate>>({});
  const [savingStage, setSavingStage] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    designStagesStore.loadStages();
  }, [designStagesStore]);

  useEffect(() => {
    const updates: Record<number, StageUpdate> = {};
    designStagesStore.stagesList.forEach((stage) => {
      updates[stage.id] = {
        name_en: stage.name_en,
      };
    });
    setStageUpdates(updates);
  }, [designStagesStore.stagesList]);

  const handleSave = async (stageId: number) => {
    const update = stageUpdates[stageId];
    if (!update) return;

    setSavingStage(stageId);
    try {
      await designStagesStore.updateStage(stageId, { name_en: update.name_en });
      toast.success("Design stage updated successfully");
    } catch (error) {
      toast.error("Failed to update design stage");
    } finally {
      setSavingStage(null);
    }
  };

  const handleFieldChange = (stageId: number, value: string | null) => {
    setStageUpdates((prev) => ({
      ...prev,
      [stageId]: { name_en: value },
    }));
  };

  const filteredStages = useMemo(() => {
    if (!searchQuery.trim()) {
      return designStagesStore.stagesList;
    }

    const query = searchQuery.toLowerCase();
    return designStagesStore.stagesList.filter((stage) => {
      return (
        stage.name.toLowerCase().includes(query) ||
        (stage.name_en && stage.name_en.toLowerCase().includes(query))
      );
    });
  }, [designStagesStore.stagesList, searchQuery]);

  if (designStagesStore.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (designStagesStore.error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4 text-destructive">
        Error loading design stages: {designStagesStore.error}
      </div>
    );
  }

  if (filteredStages.length === 0) {
    return (
      <>
        <div className="mb-4">
          <Input
            placeholder="Search design stages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
          />
        </div>
        <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground">
          {searchQuery.trim() ? "No design stages match your search." : "No design stages found."}
        </div>
      </>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Input
          placeholder="Search design stages..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1"
        />
      </div>

      <div className="rounded-md border">
        <Table className="w-full" style={{ tableLayout: "fixed" }}>
          <TableHeader>
            <TableRow>
              <TableHead style={{ width: "35%" }}>Name (Estonian)</TableHead>
              <TableHead style={{ width: "45%" }}>Name (EN)</TableHead>
              <TableHead style={{ width: "20%" }} className="text-right">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStages.map((stage) => {
              const update = stageUpdates[stage.id];
              if (!update) return null;

              const hasChanges = update.name_en !== stage.name_en;

              return (
                <TableRow key={stage.id}>
                  <TableCell style={{ width: "35%" }}>
                    <span className="text-sm font-medium">{stage.name}</span>
                  </TableCell>
                  <TableCell style={{ width: "45%" }}>
                    <Input
                      value={update.name_en || ""}
                      onChange={(e) => handleFieldChange(stage.id, e.target.value || null)}
                      placeholder="English name"
                      className="h-8 w-full"
                    />
                  </TableCell>
                  <TableCell style={{ width: "20%" }} className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSave(stage.id)}
                      disabled={!hasChanges || savingStage === stage.id}
                    >
                      {savingStage === stage.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        "Save"
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
});

export default DesignStagesTranslationsTable;
